import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Download, X, Smartphone, Apple } from "lucide-react";
import usePwaInstall from "@/lib/usePwaInstall";

const DISMISS_KEY = "jt_pwa_banner_dismissed_at";
const DISMISS_DAYS = 7;                  // re-aparece após 7 dias
const SCROLL_TRIGGER_PCT = 0.30;         // 30 % de scroll
const TIME_TRIGGER_MS = 10_000;          // 10 s de navegação

function wasDismissedRecently() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const at = parseInt(raw, 10);
    if (!Number.isFinite(at)) return false;
    const ageMs = Date.now() - at;
    return ageMs < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch { return false; }
}

function rememberDismissal() {
  try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ }
}

function ga4(name, params = {}) {
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", name, params);
    }
  } catch { /* noop */ }
}

/**
 * PwaInstallBanner — Smart Banner condicional para instalação do PWA.
 *
 * Comportamento:
 *   • Aparece SÓ em mobile (Android ou iOS).
 *   • NÃO aparece se já estiver rodando como standalone.
 *   • NÃO aparece se foi dispensado nos últimos 7 dias (localStorage).
 *   • Aparece após o PRIMEIRO de:
 *        - scroll de 30 % da página
 *        - 10 s de navegação
 *        - evento ``beforeinstallprompt`` (Android — promptable disponível)
 *   • Clique em "Instalar" (Android): dispara o prompt nativo via hook.
 *   • Clique em "Instalar" (iOS): redireciona para /app (guia visual).
 *   • Botão X dispensa por 7 dias.
 *
 * Posicionamento: rodapé mobile (fixed bottom) — não atrapalha o conteúdo
 * em scroll e não compete com o cookie banner / VLibras.
 */
export default function PwaInstallBanner() {
  const { platform, isStandalone, isInstallable, promptInstall } = usePwaInstall();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);
  const [renderable, setRenderable] = useState(false);

  // Gate de elegibilidade: mobile + não-standalone + não dispensado.
  // NÃO renderiza nas próprias páginas /app e /app/divulgar (o usuário
  // já está na trilha de instalação — banner seria ruído redundante).
  useEffect(() => {
    const onAppRoute = pathname === "/app" || pathname.startsWith("/app/");
    const eligible =
      (platform === "android" || platform === "ios") &&
      !isStandalone &&
      !wasDismissedRecently() &&
      !onAppRoute;
    setRenderable(eligible);
  }, [platform, isStandalone, pathname]);

  // Timer + scroll trigger
  useEffect(() => {
    if (!renderable) return;

    let shown = false;
    const show = () => {
      if (shown) return;
      shown = true;
      setVisible(true);
      ga4("pwa_banner_shown", { platform });
    };

    const onScroll = () => {
      const h = document.documentElement;
      const max = (h.scrollHeight - h.clientHeight) || 1;
      if (h.scrollTop / max >= SCROLL_TRIGGER_PCT) show();
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    const tid = window.setTimeout(show, TIME_TRIGGER_MS);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.clearTimeout(tid);
    };
  }, [renderable, platform]);

  // Android: se isInstallable virar true E o banner já é renderable, antecipa exibição
  useEffect(() => {
    if (renderable && isInstallable && !visible) {
      setVisible(true);
      ga4("pwa_banner_shown", { platform, trigger: "beforeinstallprompt" });
    }
  }, [renderable, isInstallable, visible, platform]);

  const handleInstall = useCallback(async () => {
    if (platform === "ios") {
      ga4("pwa_banner_click_install", { platform: "ios" });
      navigate("/app");
      return;
    }
    if (platform === "android" && isInstallable) {
      ga4("pwa_banner_click_install", { platform: "android" });
      const r = await promptInstall();
      if (r?.outcome === "accepted") {
        setVisible(false);
      } else if (r?.outcome === "unavailable") {
        // Sem prompt ainda — redireciona pro guia /app pra orientar
        navigate("/app");
      }
      return;
    }
    // Android sem prompt salvo ainda (alguns Chrome em incognito etc): /app
    navigate("/app");
  }, [platform, isInstallable, promptInstall, navigate]);

  const handleDismiss = useCallback(() => {
    rememberDismissal();
    setVisible(false);
    ga4("pwa_banner_dismissed", { platform });
  }, [platform]);

  if (!renderable || !visible) return null;

  const Icon = platform === "ios" ? Apple : Smartphone;

  return (
    <div
      role="region"
      aria-label="Instalar JurisTube como aplicativo"
      className="fixed bottom-0 inset-x-0 z-[71] md:hidden px-3 pb-3 pt-2"
      data-testid="pwa-install-banner"
    >
      <div className="mx-auto max-w-md rounded-xl border border-amber-500/30 bg-slate-900/95 backdrop-blur shadow-2xl shadow-amber-900/20">
        <div className="flex items-center gap-3 px-3 py-2.5">
          <div className="shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br from-amber-500/20 to-amber-700/10 border border-amber-500/30 flex items-center justify-center">
            <Icon className="w-4 h-4 text-amber-300" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[12px] font-semibold text-slate-100 leading-tight">
              Estude pelo App
            </div>
            <div className="text-[11px] text-slate-400 leading-snug">
              Adicione o JurisTube à sua tela inicial.
            </div>
          </div>
          <button
            type="button"
            onClick={handleInstall}
            className="shrink-0 inline-flex items-center gap-1 bg-amber-500 hover:bg-amber-400 text-slate-950 text-[11px] font-bold px-3 py-1.5 rounded-md transition active:scale-95"
            data-testid="pwa-banner-install"
          >
            <Download className="w-3 h-3" />
            Instalar
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Dispensar"
            className="shrink-0 text-slate-500 hover:text-slate-300 transition p-1"
            data-testid="pwa-banner-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
